import type { InteractiveAuthError } from "@b2bportal/card-api";
import type {
  FailedPayment,
  FailedProduct,
  PurchaseError,
} from "@b2bportal/purchase-api";
import type { CancelForAnyReasonContextV1 } from "@checkout/states/Products/AirCFARV1/context";
import type { ErrorCode, PassengerPricing } from "@hopper-b2b/types";
import type { AssignAction, EventObject } from "xstate";
import type {
  AirPFExerciseContext,
  AirPriceDropContext,
  CardPaymentContext,
  CartContext,
  CommonParentContext,
  ContactInformationContext,
  DisruptionProtectionContextV1,
  LodgingCfarContextV1,
  HtsLodgingCfarContext,
  ReviewContext,
  SeatContext,
} from "../states";
import type { AirChfarExerciseDiscountContext } from "../states/Payments/AirChfarExercise/context";
import type { WalletContext } from "../states/Payments/Wallet/context";
import type { CancelForAnyReasonContext } from "../states/Products/AirCFAR/context";
import type { AirChangeForAnyReasonContext } from "../states/Products/AirChFAR/context";
import type { DisruptionProtectionContext } from "../states/Products/AirDisruption/context";
import type { VipSupportContext } from "../states/Products/VipSupport/context";

// Move to index.ts
export * from "@checkout/states/Products/AirPriceFreeze/states/Purchase/types";

// Parent machine states
export enum ParentState {
  auth = "auth",
  route = "route",
  loading = "loading",
  metaSearchLinking = "metaSearchLinking",
  passengerInformation = "passengerInformation",
  travelerInformation = "travelerInformation",
  contactInformation = "contactInformation",
  contactAndTravelerInformation = "contactAndTravelerInformation",
  passport = "passport",
  cartQuote = "cartQuote",
  cartUpdate = "cartUpdate",
  cartUpdateForSeats = "cartUpdateForSeats",
  cartUpdateBeforeFulfill = "cartUpdateBeforeFulfill",
  seatSelection = "seatSelection",
  bagsSelection = "bagsSelection",
  payment = "payment",
  review = "review",
  cartFulfill = "cartFulfill",
  loyaltySubmit = "loyaltySubmit",
  bookingConfirmation = "bookingConfirmation",
  priceFreeze = "priceFreeze",
  priceFreezeV3 = "priceFreezeV3",
  priceFreezeDiscount = "priceFreezeDiscount",
  cancelForAnyReason = "cancelForAnyReason", // @deprecated: use cancelForAnyReasonV2 instead
  cancelForAnyReasonV1 = "cancelForAnyReasonV1", // @deprecated: use cancelForAnyReasonV2 instead
  cancelForAnyReasonV2 = "cancelForAnyReasonV2",
  lodgingCancelForAnyReasonV1 = "lodgingCancelForAnyReasonV1",
  htsLodgingCfar = "htsLodgingCfar",
  htsAirCfar = "htsAirCfar",
  changeForAnyReason = "changeForAnyReason",
  changeForAnyReasonDiscount = "changeForAnyReasonDiscount",
  insurance = "insurance",
  disruption = "disruption", // @deprecated: use disruptionV1 instead
  disruptionV1 = "disruptionV1",
  vipSupport = "vipSupport",
  cardPayment = "cardPayment",
  awardsPayment = "awardsPayment",
  guestCardPayment = "guestCardPayment",
  wallet = "wallet",
  resume = "resume",
  featureFlags = "featureFlags",
  priceDrop = "priceDrop",
  lodgingShop = "lodgingShop",
  applePay = "applePay",
  googlePay = "googlePay",
  credits = "credits",
  pointsAuth = "pointsAuth",
  applyFees = "applyFees",
  activitiesShop = "activitiesShop",
  activityInformation = "activityInformation",
}

export enum GenericChildState {
  loading = "loading",
  idle = "idle",
  route = "route",
  fetch = "fetch",
  update = "update",
  delete = "delete",
  add = "add",
  error = "error",
  saving = "saving",
  addForm = "addForm",
  updateForm = "updateForm",
  tripPricing = "tripPricing",
  wallet = "wallet",
  reQuote = "reQuote",
  addTrackingProperties = "addTrackingProperties",
}

export enum ContactInformationState {
  idle = "idle",
  createUser = "createUser",
  error = "error",
}

export enum PassengerInformationState {
  idle = "idle",
  loading = "loading",
  passengerPicker = "passengerPicker",
  passengerForm = "passengerForm",
  error = "error",
  validate = "validate",
}

export enum PassengerInformationChildState {
  idle = "idle",
  new = "new",
  edit = "edit",
  update = "update",
  delete = "delete",
  add = "add",
  validate = "validate",
  route = "route",
  fetch = "fetch",
  select = "select",
}

export enum TravelerInformationState {
  idle = "idle",
  loading = "loading",
  travelerPicker = "passengerPicker",
  travelerForm = "passengerForm",
  deleteModal = "deleteModal",
  error = "error",
  validate = "validate",
}

export enum TravelerInformationChildState {
  idle = "idle",
  new = "new",
  edit = "edit",
  update = "update",
  delete = "delete",
  add = "add",
  validate = "validate",
  route = "route",
  fetch = "fetch",
}

export enum BaggageSelectionState {
  route = "route",
  idle = "idle",
  error = "error",
}

export enum SeatSelectionState {
  route = "route",
  loading = "loading",
  idle = "idle",
  error = "error",
  validate = "validate",
  skip = "skip",
}

export enum PaymentInformationState {
  loading = "loading",
  route = "route",
  idle = "idle",
  add = "add",
  delete = "delete",
  error = "error",
  select = "select",
}

export enum PaymentInformationChildState {
  idle = "idle",
  add = "add",
  delete = "delete",
  verify = "verify",
}

export enum PassportState {
  idle = "idle",
  update = "update",
  updateAndContinue = "updateAndContinue",
  validate = "validate",
}

export enum CartQuoteState {
  discard = "discard",
  schedule = "schedule",
  polling = "polling",
  error = "error",
  route = "route",
}

export enum CartFulfillState {
  schedule = "schedule",
  pollInteractive = "pollInteractive",
  interactiveRoute = "interactiveRoute",
  challenge = "challenge",
  completeInteractive = "completeInteractive",
  pollUserActionRoute = "pollUserActionRoute",
  pollingUserAction = "pollingUserAction",
  userActionRoute = "userActionRoute",
  userAction = "userAction",
  polling = "polling",
  error = "error",
  pendingExtended = "pendingExtended",
}

export enum CartUpdateState {
  route = "route",
  update = "update",
  getBreakdown = "getBreakdown",
  error = "error",
}

export type PricingInfo = {
  fare: PassengerPricing[] | undefined;
};

export interface IContactInfo {
  countryCode?: string;
  phoneNumber: string;
  email: string;
  birthDate?: string;
  gender?: string;
}

export enum PassengerErrorModalTypes {
  InfantOnly = "InfantOnly",
  TripPricing = "TripPricing",
  UpdatePassenger = "UpdatePassenger",
  FetchPassengers = "FetchPassengers",
  ValidatePassengers = "ValidatePassengers",
  DeletePassenger = "DeletePassenger",
  ValidateAndTripPricing = "ValidateAndTripPricing",
  UnaccompaniedMinor = "UnaccompaniedMinor",
  NoPassengers = "NoPassengers",
  PassportValidationFail = "PassportValidationFail",
  ADTRequired = "ADTRequired",
  INSNotAllowed = "INSNotAllowed",
  InSufficientADTCount = "InSufficientADTCount",
  TooManyPassengers = "TooManyPassengers",
  InfantIsMinor = "InfantIsMinor",
  SearchPassengerNumNotReached = "SearchPassengerNumNotReached",
  MissingPassport = "MissingPassport",
  ExpiredPassport = "ExpiredPassport",
  MissingNationality = "MissingNationality",
  MissingPassportOrNationalId = "MissingPassportOrNationalId",
}

export enum PaymentErrorModalTypes {
  ListPaymentMethods = "ListPaymentMethods",
  VerifyPayment = "VerifyPayment",
  CardDeclined = "CardDeclined",
  ExcessiveUserActivity = "ExcessiveUserActivity",
  GenericError = "GenericError",
  DeletePayment = "DeletePayment",
}

export enum PassportErrorModalTypes {
  TripPricing = "TripPricing",
  UpdatePassenger = "UpdatePassenger",
  ValidatePassengers = "ValidatePassengers",
}

export enum ContactErrorModalTypes {
  InvalidPhoneNumber = "InvalidPhoneNumber",
}

export interface ContactError {
  type: ContactErrorModalTypes;
  data: unknown;
}

export interface PassengerError {
  type: PassengerErrorModalTypes;
  data: unknown;
}

export interface PaymentError {
  type: PaymentErrorModalTypes;
  data: ErrorCode[];
}

export interface PassportError {
  type: PassportErrorModalTypes;
  data: unknown;
}

export enum CartQuoteErrorModal {
  Invalid = "Invalid",
  Missing = "Missing",
  NoSelectedTrip = "NoSelectedTrip",
  NoProducts = "NoProducts",
  NoCipherText = "NoCipherText",
  FailedPolling = "FailedPolling",
  FailedPollingNoErrorCode = "FailedPollingNoErrorCode",
  NoEmail = "NoEmail",
  NoPhone = "NoPhone",
  Unknown = "Unknown",
}

export type CartQuoteErrorData = PurchaseError[] | InteractiveAuthError[];

export type CartQuoteError = {
  type: CartQuoteErrorModal;
  // it specifies which step the quote failed at
  failedAt?: CartQuoteState.schedule | CartQuoteState.polling;
  data?: CartQuoteErrorData;
};

export enum CartUpdateErrorModal {
  FailedPaymentAdd = "FailedPaymentAdd",
  FailedProductAdds = "FailedProductAdds",
  FailedPaymentRemoves = "FailedPaymentRemoves",
  FailedProductRemoves = "FailedProductRemoves",
  NoCipherText = "NoCipherText",
  NoEmailForProduct = "NoEmailForProduct",
  UnavailableCartState = "UnavailableCartState",
  Unknown = "Unknown",
}

export interface CartUpdateError {
  type: Array<CartUpdateErrorModal>;
  data: {
    failedPaymentAdds?: Array<FailedPayment>;
    failedProductAdds?: Array<FailedProduct>;
    failedPaymentRemoves?: Array<FailedPayment>;
    failedProductRemoves?: Array<FailedProduct>;
  };
}

export enum CartFulfillErrorModal {
  Invalid = "Invalid",
  Missing = "Missing",
  NoCipherText = "NoCipherText",
  FailedPolling = "FailedPolling",
  FailedInteractiveAuth = "FailedInteractiveAuth",
  FailedPollingNoErrorCode = "FailedPollingNoErrorCode",
  BrokenCart = "BrokenCart",
  Unknown = "Unknown",
  KcpFailure = "KcpFailure",
  RewardsPaymentFailure = "RewardsPaymentFailure",
}

export type CartFulfillErrorData = PurchaseError[] | InteractiveAuthError[];

export type CartFulfillError = {
  type: CartFulfillErrorModal;
  // it specifies which step the fulfillment failed at
  failedAt?: CartFulfillState;
  data?: CartFulfillErrorData;
};

export enum HotelLoyaltySubmitTypes {
  Failed = "Failed",
  NoLoyaltyNumberAssociatedWithUser = "NoLoyaltyNumberAssociatedWithUser",
}

export type PaymentMethodData = {
  paymentMethodId: string;
};

export enum TravelerErrorModalTypes {
  GenericError = "GenericError",
  FetchTravelers = "FetchTravelers",
  UpdateTraveler = "UpdateTraveler",
  DeleteTraveler = "DeleteTraveler",
}

export enum SeatSelectionErrorModal {
  GenericError = "GenericError",
  FailedAddToCart = "FailedAddToCart",
  BrokenCart = "BrokenCart",
}

export type SeatSelectionError = {
  type: SeatSelectionErrorModal;
  data?: unknown;
};

export enum CheckoutQueryParams {
  tripCategory = "tripCategory",
  departureLabel = "departureLabel",
  returnLabel = "returnLabel",
  tripId = "tripId",
  outgoingSliceId = "outgoingSliceId",
  outgoingFareId = "outgoingFareId",
  outgoingFareRating = "outgoingFareRating",
  returnSliceId = "returnSliceId",
  returnFareId = "returnFareId",
  returnFareRating = "returnFareRating",
  contactCountry = "contactCountry",
  contactPhone = "contactPhone",
  contactEmail = "contactEmail",
  selectedLapInfantIds = "selectedLapInfantIds",
  selectedPassengerIds = "selectedPassengerIds",
  selectedPaymentMethodId = "selectedPaymentMethodId",
  checkoutState = "checkoutState",
}

export type GenericActionTypes<Context> = Record<
  string,
  AssignAction<Context, EventObject>
>;

export interface ChildMachineConfig {
  parentMachineId: string;
  nextState?: string;
  previousState?: string;
  nextStateFromReview?: string;
  failedState?: string;
  // Overrides a child state, only the first layer, not nested states
  // This allows replacing a child state to swap your logic into an existing child machine from shared libs
  // child.states.route -> stateOverride.route
  stateOverride?: object;
}

export interface PartialParentContext extends CommonParentContext, CartContext {
  [ParentState.contactInformation]: ContactInformationContext;
  [ParentState.review]: ReviewContext;
  [ParentState.priceFreeze]?: AirPFExerciseContext;
  [ParentState.priceDrop]?: AirPriceDropContext;
  [ParentState.cancelForAnyReason]?: CancelForAnyReasonContext;
  [ParentState.cancelForAnyReasonV1]?: CancelForAnyReasonContextV1;
  [ParentState.lodgingCancelForAnyReasonV1]?: LodgingCfarContextV1;
  [ParentState.htsLodgingCfar]?: HtsLodgingCfarContext;
  [ParentState.changeForAnyReason]?: AirChangeForAnyReasonContext;
  [ParentState.changeForAnyReasonDiscount]?: AirChfarExerciseDiscountContext;
  [ParentState.disruption]?: DisruptionProtectionContext;
  [ParentState.disruptionV1]?: DisruptionProtectionContextV1;
  [ParentState.vipSupport]?: VipSupportContext;
  [ParentState.cardPayment]?: CardPaymentContext;
  [ParentState.wallet]?: WalletContext;
}
